import type { StockLocation, StockShelf, StockVariant } from '@/domains/command-center/handlers/stock/stockMode.types'
import type { SearchResultCommand } from '@/domains/command-center/types'
import type { LocationModel } from '@/domains/locations/types/models/LocationModel.types'
import type { MeasurementUnit } from '@/domains/measurement-units/types/models/MeasurementUnit.types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { IndexProductVariant } from '@/domains/variants/types/models/IndexProductVariant.types'
import type { DisplayUnit } from '@/utils/unit-util/UnitUtil.types'

import { useSkuMatcher } from '@/composables/use-sku-matcher/useSkuMatcher'
import { isProductResult } from '@/domains/command-center/utils/searchResultUtils'
import { stockUnitOfRelation } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery.utils'
import { useProductVariantHelper } from '@/domains/products/composables/use-product-variant-helper/useProductVariantHelper'
import { availableQuantity } from '@/domains/products/utils/stock-level/stockLevel'

const OPTION_SEPARATOR = ' / '

const toStockVariant = (product: IndexProduct, variant: IndexProductVariant): StockVariant => ({
  id: variant.id,
  productTitle: product.title,
  options: useProductVariantHelper().getVariantOptions(variant, OPTION_SEPARATOR),
  isTracking: variant.is_tracking_inventory === true,
  inventoryItems: variant.inventory_items ?? [],
  pricingUnit: variant.pricing_unit ?? null
})

/** A product's live variants, in the product's own order. A deleted variant holds no stock to change. */
const stockVariantsOf = (product: IndexProduct): StockVariant[] =>
  (product.variants ?? []).filter(variant => !variant.is_deleted).map(variant => toStockVariant(product, variant))

/** The live variants of a product that carry a code. A deleted one can still hold its code, and no stock. */
const stockVariantsCarrying = (product: IndexProduct, code: string): StockVariant[] =>
  useSkuMatcher()
    .findMatchingVariants(code, product)
    .filter(match => !match.matchedVariant.is_deleted)
    .map(match => toStockVariant(product, match.matchedVariant))

/**
 * The variants a scanned code names exactly, across every product the lookup returned.
 *
 * A product search also returns near matches, so only a variant holding the very code counts.
 */
const variantsMatchingScan = (results: SearchResultCommand[], code: string): StockVariant[] =>
  results
    .filter(isProductResult)
    .filter(result => result.skuMatch?.hasMatch)
    .flatMap(result => stockVariantsCarrying(result.originalData, code))

/**
 * One variant at one location.
 *
 * ⚠ **The location is matched on both spellings.** The product index sends `location`, and other
 * payloads send only `location_id`. Reading one of them made a stocked shelf read as empty.
 */
const shelfAt = (variant: StockVariant, location: StockLocation): StockShelf => {
  const item = variant.inventoryItems.find(
    candidate => (candidate.location?.id ?? candidate.location_id) === location.id
  )

  return { item: item ?? null, current: item ? availableQuantity(item) : 0 }
}

/** A variant is named by its options, and a product's only variant by the product. */
const stockVariantName = (variant: StockVariant): string =>
  variant.options ? `${variant.productTitle} · ${variant.options}` : variant.productTitle

/**
 * The unit a variant's stock reads in: its pricing unit when that holds every base unit, else the base unit. Undefined until the catalogue
 * loads, so a figure reads bare for that moment.
 *
 * Takes the catalogue rather than reading it, because the palette loads units only once stock mode opens.
 */
const stockUnitOfStockVariant = (variant: StockVariant, units: MeasurementUnit[] = []): DisplayUnit | undefined =>
  stockUnitOfRelation(units, variant.pricingUnit)

const toStockLocation = (location: LocationModel): StockLocation => ({
  id: Number(location.id),
  ulid: location.ulid,
  name: location.name
})

export {
  shelfAt,
  stockUnitOfStockVariant,
  stockVariantName,
  stockVariantsCarrying,
  stockVariantsOf,
  toStockLocation,
  variantsMatchingScan
}
